'use client';

import { Box, Typography, alpha, Stack } from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useTranslation } from './useTranslation';

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'En' },
  { code: 'te', label: 'తెలుగు', short: 'తె' },
];

/**
 * Compact language toggle for sidebars.
 * Shows a pill-style selector with flag + label.
 *
 * @param {{ variant?: 'sidebar' | 'inline', compact?: boolean }} props
 */
export function LanguageSwitch({ variant = 'sidebar', compact = false }) {
  const { language, setLanguage } = useTranslation();

  if (compact) {
    // Minimal version: just text codes in a row
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {LANGUAGES.map((lang) => {
          const isActive = language === lang.code;
          return (
            <Box
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              sx={{
                cursor: 'pointer',
                px: 1.25,
                py: 0.4,
                borderRadius: 1.5,
                bgcolor: isActive ? alpha('#000', 0.08) : 'transparent',
                border: isActive ? '1.5px solid #6b7280' : '1.5px solid transparent',
                lineHeight: 1,
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: alpha('#000', 0.06) },
              }}
              title={lang.label}
            >
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#111827' : '#6b7280',
                  lineHeight: 1,
                }}
              >
                {lang.short}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    );
  }

  // Sidebar variant: full labels
  return (
    <Box sx={{ px: variant === 'sidebar' ? 0 : 1 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
        <LanguageIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Language
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.75}>
        {LANGUAGES.map((lang) => {
          const isActive = language === lang.code;
          return (
            <Box
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                bgcolor: isActive ? alpha('#000', 0.08) : 'transparent',
                border: isActive ? '1.5px solid #374151' : '1.5px solid #e5e7eb',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: alpha('#000', 0.05), borderColor: '#9ca3af' },
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#111827' : '#6b7280',
                }}
              >
                {lang.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
